/**
 * TREATX Self-Service Legal Admin — New Document + Usage + Policies Console
 * =========================================================================
 * Lightweight companion pages to the existing AdminAgreements library.
 * Adds the three flows missing from the existing Admin UI:
 *   1. Create a brand-new legal document (metadata + draft v1) with a
 *      DOCX upload + rendered preview.
 *   2. See where a document is used (bindings + packs + evidence counts).
 *   3. Manage journey↔document bindings (add/remove/change scope) with a
 *      one-click Evaluator preview.
 *
 * The Evaluator, Recorder, Policy Sets, Packs and Reconsent engines are
 * NOT touched. This screen is a thin UI over the pre-existing admin APIs
 * plus 3 new self-service endpoints under `/api/admin/legal/*`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { toast } from 'sonner';
import {
  FileUp, PlusCircle, Loader2, ArrowLeft, Shield,
  Settings2, Play, Info,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { SafeHtml } from '../../components/security/SafeHtml';
import { Badge } from '../../components/ui/badge';
import { api } from '../../utils/api';

const shortSha = (s) => (s ? `${s.slice(0, 12)}…` : '—');
const humanDate = (iso) => (iso ? formatDateTimeLocale(iso) : '—');

// ──────────────────────────────────────────────────────────────
// Page 1 — Create New Legal Document
// ──────────────────────────────────────────────────────────────
export const NewLegalDocumentPage = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    content_key: '', title: '', doc_code: '',
    audience: 'patient', locale: 'en', internal: false,
    effective_date: '', admin_note: '',
  });

  const onUpload = async () => {
    if (!file) return toast.error('Choose a .docx file first');
    setBusy(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/admin/legal/upload-docx', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
      toast.success('DOCX rendered — review before creating');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'DOCX render failed');
    } finally {
      setBusy(false);
    }
  };

  const onCreate = async () => {
    if (!preview) return toast.error('Upload and render a DOCX first');
    if (!form.content_key || !form.title || !form.doc_code) {
      return toast.error('content_key, title, doc_code are required');
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        body_html: preview.body_html,
        source_filename: preview.source_filename,
        source_sha256: preview.source_sha256,
      };
      await api.post('/admin/legal/documents', payload);
      toast.success(`Draft created: ${form.content_key} v1`);
      setPreview(null); setFile(null);
      setForm({ ...form, content_key: '', title: '', doc_code: '', admin_note: '' });
    } catch (e) {
      const detail = e?.response?.data;
      if (detail?.code === 'DOCUMENT_ALREADY_EXISTS') {
        toast.error('That content_key already exists — use "Upload New Version" on the existing document.');
      } else {
        toast.error(detail?.message || 'Create failed');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="new-legal-document-page">
      <a href="#/treatx-admin/agreements" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-3" data-testid="ndoc-back-link">
        <ArrowLeft className="h-3 w-3" /> Back to Agreement Library
      </a>
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">New Legal Document</h1>
      <p className="text-sm text-slate-500 mb-4">
        Upload a <code>.docx</code>, review the rendered draft, then create the document.
        The document starts as <b>DRAFT</b>. It is not published until you explicitly publish it in the
        <b> Agreement Library</b>.
      </p>
      <div className="text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mb-6 text-slate-600" data-testid="ndoc-standalone-note">
        <b>Standalone document.</b> The document you create here is <b>not</b> automatically added to any Legal Pack.
        Bindings can reference it directly the moment you publish it — pack membership is optional and is managed separately
        by the pack manifest builder (only needed if you want the document to travel as part of an immutable bundle).
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Left: upload + metadata form */}
        <div className="border rounded-xl p-5 bg-white space-y-4">
          <div>
            <label className="text-xs uppercase text-slate-500">1. Upload DOCX</label>
            <div className="mt-1 flex gap-2 items-center">
              <input
                type="file" accept=".docx"
                data-testid="ndoc-file-input"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
              />
              <Button onClick={onUpload} disabled={!file || busy} data-testid="ndoc-render-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4 mr-1" />}
                Render preview
              </Button>
            </div>
          </div>

          <label className="block">
            <span className="text-xs uppercase text-slate-500">2. content_key (lowercase snake_case)</span>
            <Input value={form.content_key} onChange={(e) => setForm({ ...form, content_key: e.target.value })}
              placeholder="e.g. clinic_marketing_notice" data-testid="ndoc-content-key" />
          </label>
          <label className="block">
            <span className="text-xs uppercase text-slate-500">Title</span>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Clinic Marketing Notice" data-testid="ndoc-title" />
          </label>
          <label className="block">
            <span className="text-xs uppercase text-slate-500">Document code</span>
            <Input value={form.doc_code} onChange={(e) => setForm({ ...form, doc_code: e.target.value })}
              placeholder="e.g. CMN-1.0" data-testid="ndoc-doc-code" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase text-slate-500">Audience</span>
              <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}
                className="w-full mt-1 px-3 py-2 border rounded" data-testid="ndoc-audience">
                {['all','guest','patient','provider','clinic_admin','doctor','staff','multiple'].map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase text-slate-500">Locale</span>
              <select value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })}
                className="w-full mt-1 px-3 py-2 border rounded" data-testid="ndoc-locale">
                {['en','tr'].map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.internal}
              onChange={(e) => setForm({ ...form, internal: e.target.checked })}
              data-testid="ndoc-internal" />
            Internal document (never surfaced to public resolvers)
          </label>
          <label className="block">
            <span className="text-xs uppercase text-slate-500">Admin note (changelog)</span>
            <Textarea value={form.admin_note} onChange={(e) => setForm({ ...form, admin_note: e.target.value })}
              rows={2} placeholder="Optional note — what changed" data-testid="ndoc-note" />
          </label>

          <Button onClick={onCreate} disabled={!preview || busy} className="w-full mt-2"
            data-testid="ndoc-create-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4 mr-1" />}
            Create draft v1
          </Button>
        </div>

        {/* Right: rendered preview */}
        <div className="border rounded-xl p-5 bg-white">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase text-slate-500">Rendered draft preview</div>
            {preview && <Badge className="bg-emerald-100 text-emerald-700">{preview.render_integrity_status}</Badge>}
          </div>
          {!preview ? (
            <div className="text-sm text-slate-400 py-8 text-center">Upload a DOCX to see the preview</div>
          ) : (
            <>
              <div className="text-xs text-slate-500 space-y-0.5 mb-3">
                <div>source: <code>{preview.source_filename}</code></div>
                <div>source_sha256: <code>{shortSha(preview.source_sha256)}</code></div>
                <div>body_sha256:  <code>{shortSha(preview.body_sha256)}</code></div>
                <div>renderer: <code>{preview.renderer_version}</code></div>
                <div>body chars: {preview.body_chars} · visible: {preview.visible_chars}</div>
              </div>
              <SafeHtml
                as="article"
                html={preview.body_html}
                className="prose prose-sm max-w-none border rounded p-3 bg-slate-50 max-h-[500px] overflow-auto"
                testId="ndoc-preview-body"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};


// ──────────────────────────────────────────────────────────────
// Page 2 — Document Usage viewer
// ──────────────────────────────────────────────────────────────
export const DocumentUsagePage = () => {
  // Read `?key=...` from the hash-router query string when present.
  const initialKey = (() => {
    try {
      const hash = window.location.hash || '';
      const q = hash.split('?')[1] || '';
      const p = new URLSearchParams(q);
      return p.get('key') || 'tx_master_agreement';
    } catch { return 'tx_master_agreement'; }
  })();
  const [key, setKey] = useState(initialKey);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!key) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/legal/documents/${encodeURIComponent(key)}/usage`);
      setUsage(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Usage lookup failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="doc-usage-page">
      <a href="#/treatx-admin/agreements" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-3" data-testid="usage-back-link">
        <ArrowLeft className="h-3 w-3" /> Back to Agreement Library
      </a>
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">Document Usage</h1>
      <p className="text-sm text-slate-500 mb-4">See where a legal document is currently used — bindings, packs, reconsent policies, and consent evidence.</p>

      <div className="flex gap-2 mb-4">
        <Input value={key} onChange={(e) => setKey(e.target.value)} placeholder="content_key" className="max-w-md" data-testid="usage-key-input" />
        <Button onClick={load} disabled={loading} data-testid="usage-lookup-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Look up'}
        </Button>
      </div>

      {usage && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3 text-sm">
            {['used_by_bindings','used_by_packs','reconsent_policies','total_evidence_rows'].map((k) => (
              <div key={k} className="border rounded p-3 bg-white">
                <div className="text-xs uppercase text-slate-500">{k.replace(/_/g,' ')}</div>
                <div className="text-lg font-semibold">{usage.summary[k]}</div>
              </div>
            ))}
          </div>

          <UsageBlock title="Bindings" rows={usage.bindings} keyField="binding_id" />
          <UsageBlock title="Legal Packs" rows={usage.packs} keyField="pack_id" />
          <UsageBlock title="Reconsent Policies" rows={usage.reconsent_policies} keyField="content_key" />
          <UsageBlock title="Consent evidence by version" rows={usage.evidence_row_counts_by_version} keyField="content_version" />
        </div>
      )}
    </div>
  );
};

const UsageBlock = ({ title, rows, keyField }) => (
  <div className="border rounded-xl bg-white">
    <div className="px-4 py-2 border-b bg-slate-50 text-sm font-semibold">{title} ({rows.length})</div>
    {rows.length === 0 ? (
      <div className="p-4 text-sm text-slate-400">No entries</div>
    ) : (
      <ul className="divide-y">
        {rows.map((r, i) => (
          <li key={i} className="px-4 py-2 text-sm">
            <code className="text-xs">{String(r[keyField] ?? '(row)')}: </code>
            <span className="text-slate-600">{JSON.stringify({ ...r, [keyField]: undefined })}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);


// ──────────────────────────────────────────────────────────────
// Page 3 — Legal Policies — Business-friendly binding form
// (RC-6: no CSV, no manual journey_key/binding_id, dropdowns only)
// ──────────────────────────────────────────────────────────────
const ROLE_OPTIONS = [
  { value: 'ANY', label: 'All applicable roles' },
  { value: 'guest', label: 'Guest' },
  { value: 'patient', label: 'Patient' },
  { value: 'provider_applicant', label: 'Provider Applicant' },
  { value: 'clinic_admin', label: 'Clinic Admin' },
  { value: 'doctor', label: 'Doctor' },
  { value: 'sales_rep', label: 'Sales Representative' },
  { value: 'treatx_coordinator', label: 'Coordinator' },
  { value: 'treatx_admin', label: 'TreatX Admin' },
];
const COUNTRY_OPTIONS = [
  { value: 'ANY', label: 'All countries' },
  { value: 'TR', label: 'Türkiye' },
  { value: 'EU', label: 'EU / EEA' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'ROW', label: 'Rest of World' },
];
const PLATFORM_OPTIONS = [
  { value: 'ANY', label: 'All platforms' },
  { value: 'web', label: 'Web' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
];
const PSP_OPTIONS = [
  { value: 'ANY', label: 'Not applicable / any provider' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'iyzico', label: 'Iyzico' },
];
const MODE_OPTIONS = [
  { value: 'log', label: 'Record only (no user prompt)' },
  { value: 'warn', label: 'Show a warning' },
  { value: 'hard_block', label: 'Block the action until accepted' },
];
const GROUP_LABEL = {
  patient: 'Patient', provider: 'Provider', clinic: 'Clinic',
  commercial: 'Commercial', payment: 'Payment', messaging: 'Messaging',
  medical: 'Medical', before_after: 'Before/After', lifecycle: 'Lifecycle',
  presentation: 'Presentation', other: 'Other',
};

// LEGAL-PROCESS-CATALOG-SPRINT-1 — status badges must reflect PROVEN runtime
// truth, never aspiration. `catalog_status` comes from the backend SSOT
// (services/legal_journey_catalog.py).
const STATUS_BADGE = {
  live: { label: 'LIVE', cls: 'bg-emerald-100 text-emerald-700' },
  system: { label: 'SYSTEM', cls: 'bg-indigo-100 text-indigo-700' },
  presentation: { label: 'FOOTER', cls: 'bg-violet-100 text-violet-700' },
  not_connected: { label: 'NOT WIRED', cls: 'bg-amber-100 text-amber-800' },
  planned: { label: 'PLANNED', cls: 'bg-slate-100 text-slate-600' },
  log_only: { label: 'LOG', cls: 'bg-sky-100 text-sky-700' },
  dormant: { label: 'DORMANT', cls: 'bg-slate-100 text-slate-600' },
};

// Admin help text for the info affordance. Locale map arrives from the API
// (EN/TR parity); falls back to the process description.
const processHelp = (j, lang) => {
  const help = j?.admin_help || {};
  const l = (lang || '').split('-')[0].toLowerCase();
  return help[l] || help.en || j?.description || '';
};

// ──────────────────────────────────────────────────────────────
// LEGAL-READINESS-UI-01 — aggregate readiness card
// ──────────────────────────────────────────────────────────────
// Extends the existing Legal Self-Service surface with a one-card
// visibility summary consuming the already-implemented aggregate
// endpoints:
//   GET  /api/public/legal/readiness/status   (public overall RED/GREEN)
//   GET  /api/admin/legal/readiness           (admin-scoped detail)
// No new legal workflow, approval state, or authority is introduced.
export const LegalReadinessCard = () => {
  const { t } = useTranslation();
  const [publicStatus, setPublicStatus] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = React.useCallback(async () => {
    setBusy(true); setErr('');
    try {
      const pub = await api.get('/public/legal/readiness/status');
      setPublicStatus(pub.data || null);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'public status failed');
    }
    try {
      const adm = await api.get('/admin/legal/readiness');
      setDetail(adm.data || null);
    } catch (e) {
      // Admin endpoint is best-effort — don't clobber the public status.
      // Missing detail is not itself a failure.
    }
    setBusy(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const isReady = !!publicStatus?.is_ready;
  const code = publicStatus?.code || (isReady ? 'LEGAL_READINESS_GREEN' : 'LEGAL_READINESS_UNKNOWN');
  const badgeCls = isReady
    ? 'bg-emerald-100 text-emerald-800'
    : code === 'LEGAL_READINESS_RED'
      ? 'bg-rose-100 text-rose-800'
      : 'bg-amber-100 text-amber-800';

  const violations = detail?.violations || [];
  const documentsChecked = detail?.documents_checked || 0;
  const jurisdictions = detail?.by_jurisdiction || detail?.by_country || null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 mb-4" data-testid="legal-readiness-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-800">{t('legalPolicies.readiness.title')}</h3>
          <Badge className={badgeCls} data-testid="legal-readiness-status">{code}</Badge>
        </div>
        <Button size="sm" variant="ghost" onClick={load} disabled={busy} data-testid="legal-readiness-refresh">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : t('legalPolicies.readiness.refresh')}
        </Button>
      </div>
      <div className="text-[11px] text-slate-500 mb-2" data-testid="legal-readiness-scope-note">
        {t('legalPolicies.readiness.scopeNote')}
      </div>
      <div className="text-xs text-slate-500 mb-2">
        {t('legalPolicies.readiness.checkedAt')}{' '}
        <span data-testid="legal-readiness-checked-at">{humanDate(publicStatus?.checked_at)}</span>
      </div>

      {err && (
        <div className="text-xs text-rose-700 mb-2" data-testid="legal-readiness-error">{err}</div>
      )}

      {violations && violations.length > 0 && (
        <div className="mb-2">
          <div className="text-[11px] uppercase text-slate-500 font-semibold mb-1">
            Violations · {violations.length} across {documentsChecked} document(s)
          </div>
          <ul className="text-sm list-disc pl-4 max-h-56 overflow-auto" data-testid="legal-readiness-violations">
            {violations.slice(0, 20).map((v, i) => (
              <li key={i} className="text-xs">
                <span className="font-mono">{v.content_key}</span>{' '}
                <Badge className="bg-slate-100 text-slate-700 text-[10px]">{v.locale || '—'}</Badge>{' '}
                <span className="text-slate-700">{v.code}</span>
                {v.reason && <div className="text-[11px] text-slate-500 ml-1">{v.reason.slice(0, 220)}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {jurisdictions && (
        <div className="mb-1">
          <div className="text-[11px] uppercase text-slate-500 font-semibold mb-1">By jurisdiction/language</div>
          <div className="flex flex-wrap gap-1" data-testid="legal-readiness-jurisdictions">
            {Object.entries(jurisdictions).slice(0, 12).map(([k, v]) => (
              <Badge key={k} className={v && (v.is_ready || v === 'ready') ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                {k}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {!violations?.length && !jurisdictions && (
        <div className="text-xs text-slate-500" data-testid="legal-readiness-empty">
          No detailed readiness breakdown returned by the admin endpoint. The overall status
          above is authoritative.
        </div>
      )}
    </div>
  );
};

// TREATX-LEGAL-BINDING-CANON-01 — canonical, order-independent scope
// signature. Must mirror the backend `_scope_signature`.
const normScope = (xs) => {
  const vals = Array.from(new Set((xs || []).map((x) => String(x).trim()).filter(Boolean))).sort();
  return vals.length ? vals : ['ANY'];
};
const scopeSig = (b) => JSON.stringify([
  normScope(b.roles), normScope(b.countries), normScope(b.platforms), normScope(b.payment_providers),
]);
const scopeSummary = (t, s) => {
  const part = (vals, allKey) => {
    const n = normScope(vals);
    return n.length === 1 && n[0] === 'ANY' ? t(allKey) : n.join(', ');
  };
  return [
    part(s.roles, 'legalPolicies.scope.everyone'),
    part(s.countries, 'legalPolicies.scope.allCountries'),
    part(s.platforms, 'legalPolicies.scope.allPlatforms'),
  ].join(' · ');
};

export const LegalPoliciesPage = () => {
  const { t, i18n } = useTranslation();
  const [journeys, setJourneys] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // journey_key
  const [preview, setPreview] = useState(null);
  const [previewCtx, setPreviewCtx] = useState({ role: 'patient', country: 'TR', platform: 'web', payment_provider: 'ANY' });
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showTech, setShowTech] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);

  // Canonical process-first editor state. `scope` drives which single
  // active binding is edited; `required/optional/mode` are the payload.
  const [scope, setScope] = useState({ roles: ['ANY'], countries: ['ANY'], platforms: ['ANY'], payment_providers: ['ANY'] });
  const [requiredKeys, setRequiredKeys] = useState([]);
  const [optionalKeys, setOptionalKeys] = useState([]);
  const [mode, setMode] = useState('log');
  const loadedSigRef = React.useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [j, a] = await Promise.all([
        api.get('/admin/legal/journeys'),
        api.get('/admin/agreements'),
      ]);
      setJourneys(j.data.journeys || []);
      // DOCUMENT SELECTION SAFETY (#4): only published, non-internal,
      // non-archived docs that actually have a usable current version.
      const docs = (a.data.agreements || []).filter(
        (d) => !d.internal && d.status !== 'archived' && (d.current_version || 0) > 0,
      );
      setDocuments(docs);
    } catch (e) {
      toast.error(t('legalPolicies.toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeJourney = journeys.find((j) => j.journey_key === selected);
  const activeBindings = useMemo(
    () => (activeJourney?.bindings || []).filter((b) => b.active),
    [activeJourney],
  );
  // Exact-scope match for the current editor scope.
  const editorSig = scopeSig(scope);
  const exactScopeBindings = useMemo(
    () => activeBindings.filter((b) => scopeSig(b) === editorSig),
    [activeBindings, editorSig],
  );
  const effectiveBinding = exactScopeBindings[0] || null;
  const hasConflict = exactScopeBindings.length > 1;

  // Populate the editor whenever the selected process OR the editor scope
  // changes — preselect the effective rule's documents + mode.
  useEffect(() => {
    if (!selected) { loadedSigRef.current = null; return; }
    const sig = `${selected}|${editorSig}`;
    if (loadedSigRef.current === sig) return;
    loadedSigRef.current = sig;
    if (effectiveBinding) {
      setRequiredKeys(effectiveBinding.required_keys || []);
      setOptionalKeys(effectiveBinding.optional_keys || []);
      setMode(effectiveBinding.mode || 'log');
    } else {
      setRequiredKeys([]);
      setOptionalKeys([]);
      setMode('log');
    }
  }, [selected, editorSig, effectiveBinding]);

  const selectJourney = (jk) => {
    setSelected(jk);
    setShowAdvanced(false);
    // P0-LEGAL-SSOT-BINDING-RECONCILIATION-01 — hydrate the editor scope
    // FROM the canonical rule instead of resetting to "Everyone".
    //
    // Root cause of the reported contradiction: the editor matched rules by
    // EXACT scope signature (`scopeSig`) but always opened at
    // roles=['ANY']. A canonical rule scoped to specific roles — e.g.
    // `messaging-provider-document-share-default`
    // (roles=[provider_admin, provider_sales_rep, provider_coordinator,
    // provider_doctor, provider_after_sales_rep]) — therefore never
    // matched. The rule was listed under "Active rules" with its required
    // key, while the Required Documents checkboxes rendered EMPTY and the
    // footer said "No rule exists yet for this scope". One cause, both
    // symptoms. Saving from that state would have written a SECOND,
    // broader active rule (`upsert-canonical` only collapses siblings with
    // an identical scope signature), silently overlapping the canonical one.
    //
    // Adopting the existing rule's scope makes the hydration effect below
    // find it, so the editor opens on the canonical rule. Scope remains
    // fully editable under Advanced settings; only the default changes.
    const j = journeys.find((x) => x.journey_key === jk);
    const act = (j?.bindings || []).filter((b) => b.active);
    // Deterministic pick so the editor always opens on the same rule.
    const primary = act.length
      ? [...act].sort((a, b) => String(a.binding_id).localeCompare(String(b.binding_id)))[0]
      : null;
    setScope(primary
      ? {
        roles: normScope(primary.roles),
        countries: normScope(primary.countries),
        platforms: normScope(primary.platforms),
        payment_providers: normScope(primary.payment_providers),
      }
      : { roles: ['ANY'], countries: ['ANY'], platforms: ['ANY'], payment_providers: ['ANY'] });
    loadedSigRef.current = null;
  };

  const filteredJourneys = useMemo(() => {
    return journeys.filter((j) => {
      if (statusFilter === 'LIVE' && j.catalog_status !== 'live') return false;
      if (statusFilter === 'DORMANT' && j.catalog_status === 'live') return false;
      if (groupFilter !== 'ALL' && j.group !== groupFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return (j.label || '').toLowerCase().includes(s) || j.journey_key.toLowerCase().includes(s);
      }
      return true;
    });
  }, [journeys, search, groupFilter, statusFilter]);

  const runEvaluatorPreview = async (journeyKey) => {
    try {
      const r = await api.post('/legal/evaluate', {
        journey_key: journeyKey,
        role: previewCtx.role,
        country: previewCtx.country,
        platform: previewCtx.platform,
        payment_provider: previewCtx.payment_provider,
      });
      setPreview({ journey: journeyKey, ctx: { ...previewCtx }, result: r.data });
    } catch (e) {
      toast.error(t('legalPolicies.toast.previewFailed'));
    }
  };

  const humanTitle = (d) => (d?.title?.en) || (d?.title?.tr) || d?.content_key; // eslint-disable-line no-unused-vars

  // CANONICAL SAVE (#1): upsert exactly one active binding for this
  // process + scope. Updates in place if it exists; collapses any
  // conflicting same-scope siblings; creates one only when none exists.
  const saveRule = async () => {
    if (!selected) return toast.error(t('legalPolicies.toast.chooseProcess'));
    const meta = journeys.find((j) => j.journey_key === selected);
    if (['dormant', 'planned', 'not_connected'].includes(meta?.catalog_status)) {
      if (!window.confirm(t('legalPolicies.confirm.dormant', { label: meta.label || selected }))) return;
    }
    // ANTI-DUPLICATE GUARD (P0-LEGAL-SSOT-BINDING-RECONCILIATION-01).
    // `upsert-canonical` collapses only siblings whose scope signature is
    // IDENTICAL. Saving a scope that no existing rule matches therefore
    // ADDS an active rule. If this process already has active rules and
    // the editor is not editing one of them, say so explicitly and name
    // them, instead of silently creating an overlapping second rule.
    if (!effectiveBinding && activeBindings.length > 0) {
      const ok = window.confirm(
        t('legalPolicies.confirm.willAddOverlapping', {
          count: activeBindings.length,
          ids: activeBindings.map((b) => b.binding_id).join(', '),
          scope: scopeSummary(t, scope),
        }),
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const r = await api.post('/admin/legal/bindings/upsert-canonical', {
        journey_key: selected,
        required_keys: requiredKeys,
        optional_keys: optionalKeys,
        roles: scope.roles,
        countries: scope.countries,
        platforms: scope.platforms,
        payment_providers: scope.payment_providers,
        mode,
      });
      const deact = r.data?.deactivated_binding_ids?.length || 0;
      toast.success(deact > 0
        ? t('legalPolicies.toast.savedResolved', { count: deact })
        : t('legalPolicies.toast.saved'));
      loadedSigRef.current = null;
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (Array.isArray(d)) toast.error(d.map((x) => `${x.loc?.slice(-1)[0]}: ${x.msg}`).join('; '));
      else toast.error(d?.message || d || t('legalPolicies.toast.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // LEGAL-POLICIES-ACTIVE-RULES-UI-01 — deactivate ONE obsolete active rule
  // through the existing binding API (upsert with active:false). The row is
  // kept in the database (never deleted), nothing else is touched and
  // nothing happens without an explicit admin confirmation.
  const deactivateRule = async (b, isLast) => {
    const msg = isLast
      ? t('legalPolicies.activeRules.confirmLast', { id: b.binding_id })
      : t('legalPolicies.activeRules.confirm', { id: b.binding_id });
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      await api.post('/admin/legal/policy/bindings', {
        binding_id: b.binding_id,
        journey_key: b.journey_key,
        roles: b.roles || ['ANY'],
        countries: b.countries || ['ANY'],
        platforms: b.platforms || ['ANY'],
        payment_providers: b.payment_providers || ['ANY'],
        required_keys: b.required_keys || [],
        optional_keys: b.optional_keys || [],
        mode: b.mode || 'log',
        active: false,
      });
      toast.success(t('legalPolicies.activeRules.deactivated'));
      loadedSigRef.current = null;
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d || t('legalPolicies.activeRules.deactivateFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin"/> {t('legalPolicies.loading')}</div>;

  return (
    <div className="max-w-[1400px] mx-auto p-6" data-testid="legal-policies-page">
      <a href="#/treatx-admin/agreements" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-3" data-testid="policies-back-link">
        <ArrowLeft className="h-3 w-3" /> {t('legalPolicies.backToLibrary')}
      </a>
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">{t('legalPolicies.title')}</h1>
      <p className="text-sm text-slate-500 mb-4">{t('legalPolicies.subtitle')}</p>

      {/* GLOBAL readiness — corpus diagnostics, not per-process validity. */}
      <LegalReadinessCard />

      <div className="grid grid-cols-12 gap-6">
        <aside className="col-span-4 border rounded-xl bg-white overflow-hidden">
          <div className="px-3 py-2 border-b bg-slate-50 text-xs font-semibold">{t('legalPolicies.processes')} ({filteredJourneys.length}/{journeys.length})</div>
          <div className="px-3 py-2 border-b bg-white space-y-2">
            <Input placeholder={t('legalPolicies.searchProcess')} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="journey-search"/>
            <div className="flex gap-1 flex-wrap">
              <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} className="text-xs px-2 py-1 border rounded" data-testid="journey-group-filter">
                <option value="ALL">{t('legalPolicies.allGroups')}</option>
                {Object.entries(GROUP_LABEL).map(([k,l]) => (<option key={k} value={k}>{l}</option>))}
              </select>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-xs px-2 py-1 border rounded" data-testid="journey-status-filter">
                <option value="ALL">{t('legalPolicies.allStatuses')}</option>
                <option value="LIVE">{t('legalPolicies.liveOnly')}</option>
                <option value="DORMANT">{t('legalPolicies.dormantOnly')}</option>
              </select>
            </div>
          </div>
          <ul className="divide-y max-h-[65vh] overflow-y-auto">
            {filteredJourneys.map((j) => {
              const nActive = (j.bindings || []).filter((b) => b.active).length;
              return (
              <li key={j.journey_key}>
                <button data-testid={`journey-${j.journey_key}`}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${selected===j.journey_key?'bg-emerald-50':''}`}
                  onClick={() => selectJourney(j.journey_key)}>
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-medium text-slate-800 truncate">{j.label || j.journey_key}</span>
                    <span className="flex items-center gap-1 shrink-0">
                      {/* LEGAL-PROCESS-CATALOG-SPRINT-1 — discoverable help. */}
                      <Info
                        className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700"
                        title={processHelp(j, i18n.language)}
                        data-testid={`journey-help-${j.journey_key}`}
                      />
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_BADGE[j.catalog_status]?.cls || 'bg-slate-100 text-slate-600'}`}>
                        {STATUS_BADGE[j.catalog_status]?.label || j.catalog_status}
                      </span>
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500">{GROUP_LABEL[j.group] || 'Other'} · {t('legalPolicies.ruleCount', { count: nActive })}</div>
                </button>
              </li>
            );})}
          </ul>
        </aside>

        <main className="col-span-8 space-y-4">
          {!activeJourney && (
            <div className="border rounded-xl bg-white p-8 text-center text-sm text-slate-400" data-testid="policies-empty-state">
              {t('legalPolicies.selectPrompt')}
            </div>
          )}
          {activeJourney && (
            <>
              {/* PROCESS-FIRST canonical editor */}
              <div className="border rounded-xl bg-white p-5" data-testid="process-rule-editor">
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">{t('legalPolicies.process')}</div>
                    <h2 className="text-lg font-semibold text-slate-900" data-testid="editor-process-name">{activeJourney.label || activeJourney.journey_key}</h2>
                    <p className="text-sm text-slate-600 mt-0.5">{activeJourney.description}</p>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_BADGE[activeJourney.catalog_status]?.cls || 'bg-slate-100 text-slate-600'}`}>
                    {STATUS_BADGE[activeJourney.catalog_status]?.label || activeJourney.catalog_status}
                  </span>
                </div>

                {/* LEGAL-PROCESS-CATALOG-SPRINT-1 — proven-runtime help panel. */}
                <div className="mt-2 flex items-start gap-2 rounded border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900"
                  data-testid="process-help-panel">
                  <Info className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <div data-testid="process-help-text">{processHelp(activeJourney, i18n.language)}</div>
                    <div className="mt-1 font-mono text-[10px] text-sky-800" data-testid="process-help-behaviour">
                      {t('legalPolicies.help.behaviour')}: {t(`legalPolicies.behaviour.${activeJourney.behaviour}`, activeJourney.behaviour)}
                      {' · '}
                      {activeJourney.can_block
                        ? t('legalPolicies.help.canBlock')
                        : t('legalPolicies.help.cannotBlock')}
                    </div>
                  </div>
                </div>

                {hasConflict && (
                  <div className="mt-3 p-3 rounded border border-amber-300 bg-amber-50 text-amber-900 text-sm" data-testid="policies-conflict-banner">
                    <div className="font-semibold">{t('legalPolicies.conflict.title')}</div>
                    <div className="text-xs mt-1">{t('legalPolicies.conflict.body', { count: exactScopeBindings.length })}</div>
                    <ul className="text-[11px] font-mono mt-1 list-disc pl-4">
                      {exactScopeBindings.map((b) => (
                        <li key={b.binding_id}>{b.binding_id} — {(b.required_keys||[]).join(', ') || '∅'} [{b.mode}]</li>
                      ))}
                    </ul>
                    <div className="text-xs mt-1">{t('legalPolicies.conflict.resolveHint')}</div>
                  </div>
                )}

                {/* LEGAL-POLICIES-ACTIVE-RULES-UI-01 — individual active rules
                    for this process, with their full scope, so an obsolete
                    rule can be identified and deactivated one by one. */}
                {activeBindings.length > 0 && (
                  <div className="mt-3 border rounded-lg" data-testid="active-rules-panel">
                    <div className="px-3 py-2 border-b bg-slate-50">
                      <div className="text-xs font-semibold text-slate-700" data-testid="active-rules-title">
                        {t('legalPolicies.activeRules.title', { count: activeBindings.length })}
                      </div>
                      <div className="text-[11px] text-slate-500">{t('legalPolicies.activeRules.subtitle')}</div>
                    </div>
                    <ul className="divide-y">
                      {activeBindings.map((b) => (
                        <li key={b.binding_id} className="px-3 py-2 flex items-start justify-between gap-3"
                          data-testid={`active-rule-${b.binding_id}`}>
                          <div className="min-w-0 text-[11px] text-slate-700">
                            <div className="font-mono text-[10px] text-slate-500 break-all" data-testid={`active-rule-id-${b.binding_id}`}>
                              {b.binding_id}
                            </div>
                            <div className="mt-0.5">
                              <span className="font-medium">{t('legalPolicies.activeRules.required')}:</span>{' '}
                              <span data-testid={`active-rule-required-${b.binding_id}`}>
                                {(b.required_keys || []).join(', ') || '∅'}
                              </span>
                              {(b.optional_keys || []).length > 0 && (
                                <> · <span className="font-medium">{t('legalPolicies.activeRules.optional')}:</span>{' '}
                                  {(b.optional_keys || []).join(', ')}</>
                              )}
                            </div>
                            <div className="mt-0.5 text-slate-500" data-testid={`active-rule-scope-${b.binding_id}`}>
                              {t('legalPolicies.activeRules.role')}: {(b.roles || ['ANY']).join('/')}
                              {' · '}{t('legalPolicies.activeRules.country')}: {(b.countries || ['ANY']).join('/')}
                              {' · '}{t('legalPolicies.activeRules.platform')}: {(b.platforms || ['ANY']).join('/')}
                              {' · '}{t('legalPolicies.activeRules.psp')}: {(b.payment_providers || ['ANY']).join('/')}
                              {' · '}{t('legalPolicies.activeRules.mode')}: {b.mode}
                            </div>
                          </div>
                          <Button variant="outline" size="sm" disabled={saving}
                            className="shrink-0 border-red-300 text-red-700 hover:bg-red-50"
                            onClick={() => deactivateRule(b, activeBindings.length === 1)}
                            data-testid={`deactivate-rule-${b.binding_id}`}>
                            {t('legalPolicies.activeRules.deactivate')}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-4 space-y-4">
                  <FormRow label={t('legalPolicies.requiredDocuments')}>
                    <MultiSelectDocs value={requiredKeys} onChange={setRequiredKeys} documents={documents} testid="editor-required-docs"/>
                  </FormRow>
                  <FormRow label={t('legalPolicies.optionalDocuments')}>
                    <MultiSelectDocs value={optionalKeys} onChange={setOptionalKeys} documents={documents} testid="editor-optional-docs"/>
                  </FormRow>
                  <FormRow label={t('legalPolicies.ifNotAccepted')}>
                    <select value={mode} onChange={(e) => setMode(e.target.value)} className="w-full px-3 py-2 border rounded" data-testid="editor-mode">
                      {MODE_OPTIONS.map((m) => (<option key={m.value} value={m.value}>{t(`legalPolicies.mode.${m.value}`)}</option>))}
                    </select>
                  </FormRow>

                  {/* Applies-to summary + Advanced settings */}
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        <span className="text-xs uppercase tracking-wide text-slate-500 mr-2">{t('legalPolicies.appliesTo')}</span>
                        <span className="text-slate-800" data-testid="editor-scope-summary">{scopeSummary(t, scope)}</span>
                      </div>
                      <button className="text-xs text-emerald-700 underline inline-flex items-center gap-1" onClick={() => setShowAdvanced((s) => !s)} data-testid="editor-advanced-toggle">
                        <Settings2 className="h-3 w-3" /> {showAdvanced ? t('legalPolicies.hideAdvanced') : t('legalPolicies.advancedSettings')}
                      </button>
                    </div>
                    {showAdvanced && (
                      <div className="grid grid-cols-2 gap-3 mt-3" data-testid="editor-advanced-panel">
                        <FormRow label={t('legalPolicies.adv.roles')}>
                          <MultiSelectSimple value={scope.roles} onChange={(v) => setScope({ ...scope, roles: v })} options={ROLE_OPTIONS} testid="editor-roles"/>
                        </FormRow>
                        <FormRow label={t('legalPolicies.adv.countries')}>
                          <MultiSelectSimple value={scope.countries} onChange={(v) => setScope({ ...scope, countries: v })} options={COUNTRY_OPTIONS} testid="editor-countries"/>
                        </FormRow>
                        <FormRow label={t('legalPolicies.adv.platforms')}>
                          <MultiSelectSimple value={scope.platforms} onChange={(v) => setScope({ ...scope, platforms: v })} options={PLATFORM_OPTIONS} testid="editor-platforms"/>
                        </FormRow>
                        <FormRow label={t('legalPolicies.adv.psps')}>
                          <MultiSelectSimple value={scope.payment_providers} onChange={(v) => setScope({ ...scope, payment_providers: v })} options={PSP_OPTIONS} testid="editor-psps"/>
                        </FormRow>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3 pt-1">
                    <Button onClick={saveRule} disabled={saving} data-testid="editor-save-rule">
                      {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : <PlusCircle className="h-4 w-4 mr-1"/>}
                      {hasConflict ? t('legalPolicies.resolveAndSave') : effectiveBinding ? t('legalPolicies.saveChanges') : t('legalPolicies.createRule')}
                    </Button>
                    <span className="text-xs text-slate-500">
                      {effectiveBinding
                        ? t('legalPolicies.editingExisting')
                        : t('legalPolicies.willCreate')}
                    </span>
                  </div>
                  {showTech && effectiveBinding && (
                    <div className="text-[10px] text-slate-400 font-mono" data-testid="editor-binding-id">binding_id: {effectiveBinding.binding_id}</div>
                  )}
                </div>
              </div>

              {/* Plain-language Evaluator preview */}
              <div className="border rounded-xl bg-white p-4" data-testid="eval-preview-card">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold">{t('legalPolicies.testScenario')}</div>
                  <Button size="sm" variant="outline" onClick={() => runEvaluatorPreview(activeJourney.journey_key)} data-testid={`preview-${activeJourney.journey_key}`}>
                    <Play className="h-3 w-3 mr-1"/> {t('legalPolicies.runPreview')}
                  </Button>
                </div>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <label>{t('legalPolicies.adv.roles')}<select value={previewCtx.role} onChange={(e) => setPreviewCtx({ ...previewCtx, role: e.target.value })} className="w-full mt-1 px-2 py-1.5 border rounded">
                    {ROLE_OPTIONS.filter((r) => r.value !== 'ANY').map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select></label>
                  <label>{t('legalPolicies.adv.countries')}<select value={previewCtx.country} onChange={(e) => setPreviewCtx({ ...previewCtx, country: e.target.value })} className="w-full mt-1 px-2 py-1.5 border rounded">
                    {COUNTRY_OPTIONS.filter((r) => r.value !== 'ANY').map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select></label>
                  <label>{t('legalPolicies.adv.platforms')}<select value={previewCtx.platform} onChange={(e) => setPreviewCtx({ ...previewCtx, platform: e.target.value })} className="w-full mt-1 px-2 py-1.5 border rounded">
                    {PLATFORM_OPTIONS.filter((r) => r.value !== 'ANY').map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select></label>
                  <label>{t('legalPolicies.adv.psps')}<select value={previewCtx.payment_provider} onChange={(e) => setPreviewCtx({ ...previewCtx, payment_provider: e.target.value })} className="w-full mt-1 px-2 py-1.5 border rounded">
                    {PSP_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select></label>
                </div>
                {preview?.journey === activeJourney.journey_key && (
                  <div className="mt-3 border rounded bg-slate-50 p-3 text-sm">
                    <div className="text-xs uppercase text-slate-500 mb-1">{t('legalPolicies.result')}</div>
                    <PlainLanguageEvaluatorResult result={preview.result} documents={documents}/>
                    <button onClick={() => setShowTech((s) => !s)} className="text-xs text-slate-500 underline mt-2" data-testid="eval-tech-toggle">
                      {showTech ? t('legalPolicies.hideTech') : t('legalPolicies.showTech')}
                    </button>
                    {showTech && <pre className="text-[10px] overflow-auto mt-2">{JSON.stringify(preview.result, null, 2)}</pre>}
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

const FormRow = ({ label, children }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</div>
    {children}
  </div>
);

const MultiSelectDocs = ({ value, onChange, documents, testid }) => {
  const humanTitle = (d) => (d?.title?.en) || (d?.title?.tr) || d?.content_key;
  const toggle = (k) => onChange(value.includes(k) ? value.filter((v) => v !== k) : [...value, k]);
  return (
    <div className="border rounded max-h-40 overflow-y-auto bg-white" data-testid={testid}>
      {documents.length === 0 && <div className="p-2 text-xs text-slate-400">No published, non-internal documents available.</div>}
      {documents.map((d) => (
        <label key={d.content_key} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer">
          <input type="checkbox" checked={value.includes(d.content_key)} onChange={() => toggle(d.content_key)} data-testid={`doc-opt-${d.content_key}`}/>
          <span>{humanTitle(d)} <span className="text-xs text-slate-500">v{d.current_version || 0}</span></span>
        </label>
      ))}
    </div>
  );
};

const MultiSelectSimple = ({ value, onChange, options, testid }) => {
  const toggle = (v) => {
    if (v === 'ANY') return onChange(['ANY']);
    const next = value.filter((x) => x !== 'ANY');
    onChange(next.includes(v) ? next.filter((x) => x !== v) : [...next, v]);
  };
  return (
    <div className="border rounded p-2 text-sm bg-white" data-testid={testid}>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <label key={o.value} className="inline-flex items-center gap-1 text-xs">
            <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)}/>
            {o.label}
          </label>
        ))}
      </div>
    </div>
  );
};

const PlainLanguageEvaluatorResult = ({ result, documents }) => {
  const d = result?.decision || result;
  if (!d) return <div className="text-slate-500">No decision returned.</div>;
  const humanTitle = (ck) => {
    const doc = documents.find((x) => x.content_key === ck);
    return (doc?.title?.en) || (doc?.title?.tr) || ck;
  };
  const missing = d.missing_required || [];
  const optional = d.missing_optional || [];
  const already = d.already_accepted || [];
  const mode = d.mode || 'log';
  const modeSentence = mode === 'hard_block'
    ? 'The user will be blocked until the missing document(s) are accepted.'
    : mode === 'warn'
      ? 'A warning will be shown, but the user can proceed.'
      : 'This will be recorded silently — no user prompt.';
  return (
    <div className="space-y-2">
      {missing.length === 0 && optional.length === 0 && already.length === 0 && (
        <div className="text-emerald-700">No documents are required for this scenario — the user proceeds without any prompt.</div>
      )}
      {missing.length > 0 && (
        <div>
          <b>This process requires {missing.length} document(s):</b>
          <ol className="list-decimal list-inside mt-1 text-sm">
            {missing.map((m, i) => (
              <li key={i}>
                {humanTitle(m.content_key || m)} {m.current_version ? `— v${m.current_version}` : ''}
                <div className="text-xs text-slate-500 ml-4">Status: {m.status || 'NEEDS_ACCEPTANCE'} — Action: Required</div>
              </li>
            ))}
          </ol>
        </div>
      )}
      {already.length > 0 && (
        <div className="text-sm text-emerald-700">Already accepted (current version): {already.map((a) => humanTitle(a.content_key || a)).join(', ')}</div>
      )}
      <div className="text-sm text-slate-800 mt-2"><b>Final outcome:</b> {modeSentence}</div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// (obsolete developer-form removed — see the business form above)
// ──────────────────────────────────────────────────────────────
export default {
  NewLegalDocumentPage,
  DocumentUsagePage,
  LegalPoliciesPage,
};
